import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

/**
 * Synthèses mensuelles de la direction (phase 5, TF-440) : produites le 1er du
 * mois pour le mois écoulé, figées, envoyées aux comptes de la direction. Le
 * contenu arrive prêt à afficher (`domain/synthese-mensuelle.ts`) : textes,
 * écarts et tons ; l'écran ne recalcule rien.
 */

export type TonEcart = 'success' | 'danger' | 'neutral';

export interface IndicateurSynthese {
  id: string;
  libelle: string;
  valeur: number | null;
  texte: string;
  detail: string | null;
  comparaison: {
    valeur: number | null;
    valeurTexte: string;
    texte: string;
    ecart: string | null;
    ton: TonEcart;
  } | null;
}

export interface TableauSynthese {
  id: string;
  titre: string;
  colonnes: { libelle: string; alignement: 'gauche' | 'droite' }[];
  lignes: { cellules: string[]; total: boolean }[];
  vide: string;
}

export interface SectionSynthese {
  id: 'ACTIVITE' | 'SATISFACTION' | 'ABSENTEISME' | 'FINANCEURS' | 'IMPACT';
  titre: string;
  lecture: string;
  indicateurs: IndicateurSynthese[];
  situation: IndicateurSynthese[];
  tableaux: TableauSynthese[];
  lien: { libelle: string; chemin: string };
}

export interface ContenuSynthese {
  format: number;
  mois: string;
  libelle: string;
  du: string;
  au: string;
  precedent: { mois: string; libelle: string };
  situationAu: string;
  antennes: { code: string; nom: string }[];
  sections: SectionSynthese[];
}

export interface Destinataire {
  nom: string;
  email: string;
}

export interface VueSynthese {
  id: string;
  mois: string;
  titre: string;
  version: number;
  origine: 'AUTOMATIQUE' | 'MANUELLE';
  origineLibelle: string;
  genereeLe: string;
  genereePar: string | null;
  envoi: { le: string; par: string | null; destinataires: Destinataire[] } | null;
}

export interface ListeSyntheses {
  mois: { mois: string; titre: string; versions: VueSynthese[] }[];
  generables: { mois: string; titre: string; versions: number }[];
  prochaine: { mois: string; titre: string; le: string };
  destinataires: Destinataire[];
  expedition: EtatExpedition;
}

const CLE = ['syntheses-mensuelles'];

export function useSynthesesMensuelles() {
  return useQuery({
    queryKey: CLE,
    queryFn: () => appelApi<ListeSyntheses>('/syntheses-mensuelles'),
  });
}

export function useSyntheseMensuelle(id: string | null) {
  return useQuery({
    queryKey: [...CLE, id],
    queryFn: () => appelApi<{ synthese: VueSynthese; contenu: ContenuSynthese }>(`/syntheses-mensuelles/${id}`),
    enabled: Boolean(id),
  });
}

function useApresGeste() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CLE });
    // Le tableau de bord de la direction montre la dernière synthèse.
    void client.invalidateQueries({ queryKey: ['financements', 'tableau-de-bord-direction'] });
  };
}

export function useGenererSynthese() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (mois: string) =>
      appelApi<VueSynthese>('/syntheses-mensuelles', { methode: 'POST', corps: { mois } }),
    onSuccess: apres,
  });
}

export function useEnvoyerSynthese() {
  const apres = useApresGeste();
  return useMutation({
    mutationFn: (id: string) =>
      appelApi<VueSynthese & { prevenus: number }>(`/syntheses-mensuelles/${id}/envoi`, { methode: 'POST' }),
    onSuccess: apres,
  });
}

/** Le PDF se lit par une route gardée et tracée ; `consulter` l'ouvre dans le navigateur. */
export function urlPdfSynthese(id: string, consulter = false): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/syntheses-mensuelles/${id}/pdf${consulter ? '?consulter=1' : ''}`;
}
